<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import SetupFlowShell from '@/components/shared/SetupFlowShell.vue'
import { useAuthStore } from '@/stores/authStore'
import { qboProvider } from '@/api/integrations/qbo/provider.js'
import { Alert, Badge, Button, Card, Spinner } from '@/components/ui'
import { track } from '@/lib/usageTracker'
import { ArrowRight, Landmark, RefreshCw, Store } from 'lucide-vue-next'

// --- Interface-design checkpoint ---
// Intent: OAuth-callback landing surface. Surfaces connection state
// (success vs. error) and routes the user to dashboard or back to
// connect. The shell gives it a consistent header + provenance rail.

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()

const status = ref('')
const realmId = ref('')
const errorMessage = ref('')
const storeId = ref('')
const invoiceNumber = ref('')
const redirecting = ref(false)

function parseQuery() {
  const q = route.query
  status.value = (q.status?.toString() || '').toLowerCase()
  realmId.value = q.realmId?.toString() || ''
  const PREFIX = qboProvider.localStoragePrefix

  // Login-resume context can come from either the URL query or the
  // Pinia auth store. We prefer store order (URL → store → local
  // fallback) so query params still override when both are present.
  storeId.value =
    q.storeId?.toString() ||
    auth.selectedStore?.storeId ||
    auth.storeID ||
    localStorage.getItem(`${PREFIX}.storeId`) ||
    ''
  invoiceNumber.value =
    q.invoiceNumber?.toString() ||
    auth.invoiceNumber ||
    localStorage.getItem(`${PREFIX}.invoiceNumber`) ||
    ''

  const msg = q.error?.toString() || q.message?.toString() || ''
  if (status.value === 'error') {
    errorMessage.value = msg || 'Authorization failed or was cancelled.'
  }
}

function tryAgain() {
  track('btn:TryAgainQbo', 'click')
  const target: { name: string; query: Record<string, string> } = auth.hasUserContext
    ? { name: 'StoreSelection', query: {} }
    : { name: 'ActivateQbo', query: {} }
  if (storeId.value) target.query.storeId = storeId.value
  if (invoiceNumber.value) target.query.invoiceNumber = invoiceNumber.value
  router.replace(target)
}

function goToDashboard() {
  track('btn:GoToDashboardQbo', 'click')
  redirecting.value = true
  router.push({ name: 'Dashboard' })
}

const contextItems = computed(() => [
  {
    label: 'Store',
    value: storeId.value || 'Missing from callback',
    tone: storeId.value ? 'default' : 'warning',
  },
  {
    label: 'Invoice',
    value: invoiceNumber.value || 'Missing from callback',
    tone: invoiceNumber.value ? 'default' : 'warning',
  },
  {
    label: 'Realm ID',
    value: realmId.value || 'Not yet issued',
    tone: realmId.value ? 'success' : 'warning',
  },
])

onMounted(async () => {
  parseQuery()

  const apiKey = route.query.apiKey?.toString()
  const integrationId = route.query.integrationId

  if (status.value === 'error') {
    auth.clearIntegration(qboProvider.id)
  }

  if (apiKey) {
    auth.setIntegrationResult({
      provider: qboProvider.id,
      apiKey,
      integrationId: integrationId ? Number(integrationId) || undefined : undefined,
      authStatus: 'ready',
    })
    auth.selectPackage(qboProvider.id)
    // No redirect here; the success panel shows the result and the
    // user explicitly moves to the dashboard via the action button.
  }
})
</script>

<template>
  <SetupFlowShell
    eyebrow="Connection result"
    :title="`${qboProvider.name} connection`"
    subtitle="Review the result of the OAuth handoff. The store and invoice references are bound to the new connection."
    :provider-name="qboProvider.name"
    provider-color="#2ca01c"
    :context-items="contextItems"
  >
    <Card padding="lg" class="qbo-success">
      <template v-if="status === 'error'">
        <div class="qbo-success__head">
          <div class="qbo-success__mark qbo-success__mark--error" aria-hidden="true">
            <RefreshCw :size="18" />
          </div>
          <div>
            <div class="qbo-success__eyebrow">Connection error</div>
            <h2 class="qbo-success__title">We could not complete the connection</h2>
          </div>
          <Badge variant="error">Disconnected</Badge>
        </div>

        <Alert id="qboError" variant="error" title="Intuit did not grant access">
          {{ errorMessage }}
        </Alert>

        <div class="qbo-success__actions">
          <Button id="tryAgain" @click="tryAgain">Try again</Button>
        </div>
      </template>

      <template v-else>
        <div class="qbo-success__head">
          <div class="qbo-success__mark" aria-hidden="true">
            <Landmark :size="18" />
          </div>
          <div>
            <div class="qbo-success__eyebrow">Acting context</div>
            <h2 class="qbo-success__title">Connection ready</h2>
          </div>
          <Badge variant="success">Connected</Badge>
        </div>

        <p class="qbo-success__copy">
          {{ qboProvider.name }} integration is now active for store
          <strong>{{ storeId || '—' }}</strong>. Move to the dashboard to start syncing.
        </p>

        <div class="qbo-success__summary">
          <div class="summary-row">
            <span class="summary-label"><Store :size="14" /> Store reference</span>
            <span class="summary-value">{{ storeId || '—' }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Invoice number</span>
            <span class="summary-value">{{ invoiceNumber || '—' }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Realm ID</span>
            <span class="summary-value">{{ realmId || '—' }}</span>
          </div>
        </div>

        <div class="qbo-success__actions">
          <Button id="goToDashboard" :disabled="redirecting" @click="goToDashboard">
            <Spinner v-if="redirecting" size="sm" />
            <span v-if="!redirecting">Go to Dashboard</span>
            <span v-else>Opening</span>
            <ArrowRight v-if="!redirecting" :size="14" />
          </Button>
          <Button id="reconnect" variant="secondary" @click="() => { track('btn:ReconnectQbo', 'click'); tryAgain() }">Reconnect</Button>
        </div>
      </template>
    </Card>
  </SetupFlowShell>
</template>

<style scoped>
.qbo-success {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.qbo-success__head {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.qbo-success__mark {
  width: 2.4rem;
  height: 2.4rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border-soft);
  background: var(--color-surface-muted);
  color: var(--color-ink);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.qbo-success__mark--error {
  background: var(--color-error);
  color: var(--color-paper);
  border-color: var(--color-error);
}

.qbo-success__eyebrow {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-ink-soft);
}

.qbo-success__title {
  margin: 0.2rem 0 0;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--color-ink);
}

.qbo-success__copy {
  margin: 0;
  font-size: 0.9rem;
  color: var(--color-ink-soft);
}

.qbo-success__summary {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;
  border-top: 1px solid var(--color-border-soft);
  border-bottom: 1px solid var(--color-border-soft);
  padding: 0.9rem 0;
}

.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.summary-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-ink-soft);
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.summary-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-ink);
  font-variant-numeric: tabular-nums;
  text-align: right;
  word-break: break-all;
}

.qbo-success__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.qbo-success__actions :deep(button) {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

@media (max-width: 640px) {
  .qbo-success__actions :deep(button) {
    width: 100%;
    justify-content: center;
  }
}
</style>
